import { requireNonNullable } from '@disy/lib/custom-error';
import { getDisyNamespace } from '@disy/lib/disy';
import { getLocation } from '@disy/lib/location';

export type CadenzaUrl = string;

/*
 * Prefixes relative URLs with the target origin and the context path and maintains the jsessionid param.
 *
 * We need to return an absolute URL, because we want to support also context paths that are equal to
 * relative URLs used in Cadenza (like 'repositories', 'workbooks' or whatever else). By using absolute URLs,
 * we know that the context path is already included correctly and does not need to be added again.
 * This can happen, if this function is called again later on in the same handling.
 *
 * See net/disy/cadenza/web/util/UrlEncodingUtilities.java
 */
export function cadenzaUrl(url: string): CadenzaUrl {
  if (!url) {
    return '';
  }

  if (hasScheme(url)) {
    if (!hasAllowedScheme(url)) {
      return '';
    }

    const jsessionid = getJsessionidParam(getLocation().href);
    if (isExternal(url) && jsessionid) {
      return removeCadenzaJsessionidParam(url, jsessionid);
    }
  } else {
    return getLocation().origin + getDisyNamespace().contextPath + url;
  }

  return url;
}

/**
 * Returns true if the given URL includes an origin (i.e. it is not a relative URL)
 * and it's different from the current page's origin,
 * or if the origins match but the path of the given URL does not start with
 * the current Cadenza context path, as defined in the global Disy object.
 * Always returns false for relative URLs.
 * See the tests for examples.
 * @param urlStr The URL to check.
 */
export function isExternal(urlStr: string) {
  if (!hasScheme(urlStr)) {
    return false;
  }

  const url = new URL(urlStr);
  const currentOrigin = new URL(getLocation().href).origin;
  return url.origin !== currentOrigin || !matchesContextPath(url.pathname);
}

export function hasScheme(url: string) {
  return URL.canParse(url);
}

const ALLOWED_SCHEME_VALUES = ['http', 'https', 'data', 'blob', 'mailto'] as const;
export type AllowedScheme = (typeof ALLOWED_SCHEME_VALUES)[number];
export const AllowedScheme = { values: ALLOWED_SCHEME_VALUES } as const;

export function hasAllowedScheme(url: string) {
  return hasScheme(url) && AllowedScheme.values.includes(new URL(url).protocol.slice(0, -1) as AllowedScheme);
}

function matchesContextPath(path: string) {
  const contextPath = requireNonNullable(getDisyNamespace().contextPath);
  return path === contextPath || path.startsWith(contextPath + '/');
}

function removeCadenzaJsessionidParam(url: string, jsessionid: string) {
  return url.replace(`;jsessionid=${jsessionid}`, '');
}

function getJsessionidParam(url: string) {
  return url.match(/;jsessionid=(\w+)/)?.[1];
}
